import { useCallback, useEffect, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { Check, Loader2, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { Header } from "@/components/Header";
import { OrderBreakdown } from "@/components/OrderBreakdown";
import { api, errMsg, rupiah, STATUS, tableLabel, formatTime } from "@/lib/api";

const FLOW = [
  { key: "menunggu_konfirmasi", label: "Pembayaran dikirim", sub: "Menunggu staf mengecek pembayaran" },
  { key: "diproses", label: "Pembayaran dikonfirmasi", sub: "Pesananmu sedang disiapkan" },
  { key: "diantar", label: "Diantar ke meja", sub: "Staf sedang menuju mejamu" },
  { key: "selesai", label: "Selesai", sub: "Selamat menikmati!" },
];

const Timeline = ({ status, free }) => {
  const flow = free ? FLOW.slice(1).map((f, i) => (i === 0 ? { ...f, label: "Dibayar penuh dengan Coin" } : f)) : FLOW;
  const idx = flow.findIndex((f) => f.key === status);
  return (
    <ol className="mt-6 space-y-0" data-testid="order-timeline">
      {flow.map((f, i) => {
        const done = i <= idx;
        return (
          <li key={f.key} className="flex gap-4">
            <div className="flex flex-col items-center">
              <span className={`w-8 h-8 rounded-full grid place-items-center border-2 ${done ? "bg-forest border-forest text-paper" : "border-sage-200 bg-paper"}`}>
                {done ? <Check className="w-4 h-4" /> : <span className="w-2 h-2 rounded-full bg-sage-200" />}
              </span>
              {i < flow.length - 1 && <span className={`w-0.5 h-10 ${i < idx ? "bg-forest" : "bg-sage-200"}`} />}
            </div>
            <div className="pt-1"><div className={`font-semibold ${done ? "text-ink" : "text-muted2"}`}>{f.label}</div><div className="text-xs text-muted2">{f.sub}</div></div>
          </li>
        );
      })}
    </ol>
  );
};

export default function OrderStatusPage() {
  const { id } = useParams();
  const [params] = useSearchParams();
  const token = params.get("t") || "";
  const [order, setOrder] = useState(null);

  const load = useCallback(() => {
    api.get(`/orders/${id}`, { params: { token } }).then((r) => setOrder(r.data)).catch((e) => toast.error(errMsg(e)));
  }, [id, token]);

  useEffect(() => {
    load();
    const t = setInterval(load, 8000);
    return () => clearInterval(t);
  }, [load]);

  if (!order) return (<div className="min-h-screen bg-cream"><Header /><div className="grid place-items-center py-32"><Loader2 className="w-8 h-8 animate-spin text-forest" /></div></div>);
  const st = STATUS[order.status];

  return (
    <div className="min-h-screen bg-cream">
      <Header />
      <main className="max-w-lg mx-auto px-4 py-10 rise" data-testid="order-status-page">
        <div className="flex items-center justify-between">
          <span className="text-xs font-semibold tracking-[0.15em] text-forest">PESANAN {order.code}</span>
          <button onClick={load} className="text-sm flex items-center gap-1.5 text-forest" data-testid="order-refresh-button"><RefreshCw className="w-4 h-4" /> Perbarui</button>
        </div>
        <h1 className="font-serif font-semibold text-4xl mt-2">{order.status === "selesai" ? "Terima kasih!" : "Pesananmu tercatat"}</h1>
        <p className="text-muted2 mt-2">Gerai {order.outlet_name} · {tableLabel(order.floor, order.table)} · {formatTime(order.created_at)}</p>
        <span className={`inline-block mt-4 text-sm font-semibold px-3 py-1 rounded-full border ${st.cls}`} data-testid="order-status-badge">{st.label}</span>
        {order.coins_earned > 0 && order.status !== "dibatalkan" && (
          <p className="text-sm text-forest mt-3" data-testid="order-status-coins">{order.coins_awarded ? `+${order.coins_earned} Coin sudah masuk ke akunmu.` : `+${order.coins_earned} Coin akan masuk setelah pembayaran dikonfirmasi.`}</p>
        )}
        {order.status === "menunggu_pembayaran" && (
          <Link to={`/bayar/${id}?t=${token}`} className="btn-forest block text-center py-3.5 mt-6" data-testid="order-continue-payment-link">Lanjut Pembayaran</Link>
        )}
        {order.status === "dibatalkan" ? (
          <p className="mt-6 text-sm text-red-700">Pesanan ini dibatalkan. Silakan hubungi staf kami jika ada pertanyaan.</p>
        ) : order.status !== "menunggu_pembayaran" && <Timeline status={order.status} free={order.total === 0} />}
        <div className="card-paper p-5 mt-8">
          {order.items.map((i) => <div key={i.menu_id} className="py-1.5"><div className="flex justify-between"><span>{i.qty}× {i.name}</span><span>{rupiah(i.qty * i.price)}</span></div>{i.note && <div className="text-xs text-muted2">Catatan: {i.note}</div>}</div>)}
          {order.notes && <p className="text-xs text-muted2 mt-2">Catatan: {order.notes}</p>}
          <div className="border-t border-sage-200 mt-3 pt-3"><OrderBreakdown o={order} testid="order-status-breakdown" /></div>
          <div className="flex justify-between border-t border-sage-200 mt-3 pt-3 font-bold"><span>Total · {order.payment_method === "qris" ? "QRIS" : "Transfer Bank"}</span><span data-testid="order-status-total">{rupiah(order.total)}</span></div>
        </div>
        <Link to="/" className="block text-center text-sm text-forest font-semibold mt-6" data-testid="order-back-to-menu">Pesan lagi</Link>
      </main>
    </div>
  );
}
